/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// RN + NativeWind primitives that back the redesigned shared Settings
// modal. Pinned to Figma file Y95LG1oFzU2PHIvFuMYTfJ, page "Settings
// modal" — specifically:
//
//   * Input field   — node I3542:4433;761:3102
//     36px tall, padding 16h/8v, 12px radius,
//     fill #15171C, 1px border #22242A.
//   * Button "sm"   — node 3542:4431 / 3542:4434
//     30px height, padding 12h/4v, 8px radius, gap 8.
//   * Button "md"   — node 3542:4488 (destructive "Sign out")
//     36px height, padding 16h/8v, 8px radius.
//   * Toggle/Switch — node 3542:4347. 34×20 track, full-pill radius.
//     On=#537EFF, off=#15171C. Thumb 16×16 fill #E8EAED, 2px inset,
//     subtle drop shadow.
//   * Select pill   — same surface as the input field with chevron
//     suffix. RN-macos has no real dropdown primitive yet — taps
//     cycle through options.
//
// Each visual element carries `dark:` overrides on top of `light-*`
// token classes so the modal renders correctly in either theme. The
// host flips `<html class="dark">` via PreferencesStore.applyTheme();
// NativeWind's css-interop sees the class change and re-renders.

import { ChevronDown } from "lucide-react-native";
import type { ReactNode } from "react";
import { Pressable, Text, TextInput, View } from "react-native";

interface TextFieldProps {
	readonly value: string;
	readonly onChange: (next: string) => void;
	readonly placeholder?: string;
	readonly label?: string;
	readonly helper?: string;
	// Mirrors the web `<input type=...>` attribute. Only the values we
	// actually use on desktop map onto RN's `keyboardType` /
	// `secureTextEntry` — the rest are accepted but ignored, matching
	// the web Tailwind file's API surface for prop-compat.
	readonly type?: "text" | "email" | "tel" | "password";
	readonly autoComplete?: string;
	readonly disabled?: boolean;
	readonly id?: string;
	readonly maxLength?: number;
}

export function TextField(props: TextFieldProps): JSX.Element {
	const {
		value,
		onChange,
		placeholder,
		label,
		helper,
		type = "text",
		disabled,
		maxLength,
	} = props;

	const isPassword = type === "password";
	const keyboardType =
		type === "email"
			? "email-address"
			: type === "tel"
				? "phone-pad"
				: "default";

	return (
		<View className="flex flex-col gap-1 w-full">
			{label != null && (
				// Figma "Title" label inside the input field component:
				// Lato Regular 12/16.8, color #B5BAC3.
				<Text
					className="
						text-[12px] leading-[16px] font-normal
						text-light-text-secondary dark:text-[#B5BAC3]
					"
				>
					{label}
				</Text>
			)}
			<TextInput
				value={value}
				onChangeText={onChange}
				placeholder={placeholder}
				// Placeholder pulled from the design-system tertiary token
				// — Figma uses #6B7280 on dark, light surfaces use the
				// equivalent tertiary text token.
				placeholderTextColor={disabled ? "#6B7280" : "#6B7280"}
				editable={!disabled}
				secureTextEntry={isPassword}
				keyboardType={keyboardType}
				maxLength={maxLength}
				// Figma input field surface (node I3542:4433;761:3102):
				// h-36px, padding 16h/8v, radius 12, fill #15171C, border
				// #22242A. Body text 14/20 #F5F6F8.
				className="
					h-9 px-4
					rounded-xl border
					bg-light-surface-secondary dark:bg-[#15171C]
					border-light-border-secondary dark:border-[#22242A]
					text-[14px] leading-[20px] font-normal
					text-light-text-primary dark:text-[#F5F6F8]
					outline-none
					focus:border-light-accent-sky focus:dark:border-[#537EFF]
					disabled:opacity-60
				"
			/>
			{helper != null && helper.length > 0 && (
				<Text
					className="
						text-[10px] leading-[14px] font-normal
						text-light-text-tertiary dark:text-[#6B7280]
					"
				>
					{helper}
				</Text>
			)}
		</View>
	);
}

interface ButtonProps {
	readonly onClick: () => void;
	readonly variant?: "primary" | "secondary" | "destructive" | "ghost";
	readonly disabled?: boolean;
	readonly busy?: boolean;
	readonly children: ReactNode;
	readonly size?: "sm" | "md";
	readonly type?: "button" | "submit";
}

// Figma button variants — derived from the "_Buttons" component set
// shared by Account, Sessions, and Notifications panes. The default
// "sm" size matches the 30px "Change" / "Save" buttons rendered next
// to inputs (Figma 3542:4431 — 78×30, radius 8, padding 12h/4v).
// The "md" size is the 36px destructive button used on the Sessions
// pane ("Sign out of all devices").
const BUTTON_VARIANT_CLASSES: Record<
	NonNullable<ButtonProps["variant"]>,
	string
> = {
	// Brand blue — Figma color variable accent.500 → #537EFF.
	primary:
		"bg-light-accent-sky dark:bg-[#537EFF] hover:opacity-90 disabled:opacity-50",
	// Surface with hairline border — Figma uses the same fill as the
	// input field (#15171C / #22242A) for "secondary" buttons.
	secondary:
		"bg-light-surface-secondary dark:bg-[#15171C] border border-light-border-secondary dark:border-[#22242A] hover:opacity-90 disabled:opacity-50",
	// Destructive — Figma destructive button fill `#FF5353`
	// (rgb 255, 83, 83 in node 3542:4488).
	destructive: "bg-[#FF5353] hover:opacity-90 disabled:opacity-50",
	// Ghost / link-style — no fill, subtle hover wash.
	ghost:
		"bg-transparent hover:bg-black/[0.04] dark:hover:bg-white/[0.04] disabled:opacity-40",
};

// Per-variant text-color classes. RN's <Text> doesn't inherit color
// from its <Pressable> ancestor — pin the label color so each variant
// reads correctly across themes.
const BUTTON_TEXT_CLASSES: Record<NonNullable<ButtonProps["variant"]>, string> =
	{
		primary: "text-white",
		secondary: "text-light-text-primary dark:text-[#F5F6F8]",
		destructive: "text-white",
		ghost: "text-light-text-secondary dark:text-[#B5BAC3]",
	};

export function PillButton(props: ButtonProps): JSX.Element {
	const {
		onClick,
		variant = "primary",
		disabled,
		busy,
		children,
		size = "sm",
	} = props;
	const isDisabled = disabled || busy;
	const variantClass = BUTTON_VARIANT_CLASSES[variant];
	const textClass = BUTTON_TEXT_CLASSES[variant];
	// "sm" = Figma's 30px "Change" pill (3542:4431: 78×30, padding
	//        12h/4v, radius 8, gap 8, text Lato 14/20 400).
	// "md" = Figma's 36px destructive CTA (3542:4488: padding 12h/4v,
	//        radius 8, text Lato 14/20 700).
	const sizeClass = size === "md" ? "h-9 px-3" : "h-[30px] px-3";
	// Figma's small Change buttons use Lato Regular (400); the larger
	// destructive button uses Bold (700). Pin font-weight per size so
	// the small variant doesn't look heavy next to a 14px input label.
	const fontClass = size === "md" ? "font-bold" : "font-normal";

	return (
		<Pressable
			accessibilityRole="button"
			onPress={() => {
				if (!isDisabled) onClick();
			}}
			disabled={isDisabled}
			className={`
				inline-flex flex-row items-center justify-center gap-2
				${sizeClass}
				rounded-lg
				transition-colors
				${variantClass}
			`}
		>
			<Text
				className={`text-[14px] leading-[20px] ${fontClass} ${textClass}`}
			>
				{busy ? "…" : (children as ReactNode)}
			</Text>
		</Pressable>
	);
}

interface ToggleProps {
	readonly checked: boolean;
	readonly onChange: (next: boolean) => void;
	readonly label?: string;
	readonly disabled?: boolean;
	readonly id?: string;
}

// Figma Toggle (node 3542:4347): 34×20 track, full-pill radius.
// On-state fill = brand blue #537EFF; off-state = #15171C (same
// dark fill as the input field). Thumb = 16×16, fill #E8EAED, 2px
// inset, with a subtle drop shadow. Light-mode variants use the
// tokenized border to keep the off-state visible against the
// light surface.
export function Toggle(props: ToggleProps): JSX.Element {
	const { checked, onChange, label, disabled } = props;

	return (
		<View
			className={`
				inline-flex flex-row items-center gap-3
				${disabled ? "opacity-50" : ""}
			`}
		>
			<Pressable
				accessibilityRole="switch"
				accessibilityState={{ checked, disabled: Boolean(disabled) }}
				disabled={disabled}
				onPress={() => onChange(!checked)}
				className={`
					relative inline-flex h-5 w-[34px] shrink-0
					rounded-full
					transition-colors
					${
						checked
							? "bg-light-accent-sky dark:bg-[#537EFF]"
							: "bg-light-surface-tertiary dark:bg-[#15171C] border border-light-border-secondary dark:border-[#22242A]"
					}
				`}
			>
				<View
					className="
						absolute top-[2px]
						h-4 w-4 rounded-full
						bg-white dark:bg-[#E8EAED]
					"
					// RN-macos can't compute Tailwind's `translate-x-*`
					// values reliably — pin the thumb position via inline
					// style so both platforms see the same offset.
					// Figma 3542:4347: paddingLeft 16 when on, paddingLeft 2
					// when off; track 34 - thumb 16 - 2 inset = 16. The
					// border on the off-state adds 1px so we use top: 1.
					style={{
						left: checked ? 16 : 2,
						top: checked ? 2 : 1,
						shadowColor: "#000",
						shadowOffset: { width: 0, height: 1 },
						shadowOpacity: 0.2,
						shadowRadius: 2,
					}}
				/>
			</Pressable>
			{label != null && (
				<Text
					className="
						text-[14px] leading-[20px] font-normal
						text-light-text-primary dark:text-[#F5F6F8]
					"
				>
					{label}
				</Text>
			)}
		</View>
	);
}

// Select substitute. RN has no native `<select>` primitive that
// matches the web's dropdown menu — rather than ship a portal +
// measure solution here, the control renders the currently-selected
// label as a chip + a tap cycles to the next option. The Figma
// frame's dropdown affordance is preserved by the chevron caret;
// behavioural equivalence comes in a follow-up that lands a proper
// Menu primitive in @ping/ui.
interface SelectProps<T extends string> {
	readonly value: T;
	readonly onChange: (next: T) => void;
	readonly options: readonly { readonly value: T; readonly label: string }[];
	readonly disabled?: boolean;
	readonly id?: string;
}

export function Select<T extends string>(props: SelectProps<T>): JSX.Element {
	const { value, onChange, options, disabled } = props;
	const current = options.find((o) => o.value === value) ?? options[0];

	const cycle = (): void => {
		if (options.length === 0) return;
		const idx = options.findIndex((o) => o.value === value);
		const next = options[(idx + 1) % options.length];
		if (next) onChange(next.value);
	};

	return (
		<Pressable
			accessibilityRole="button"
			disabled={disabled}
			onPress={cycle}
			className="
				h-9 pl-4 pr-3
				rounded-xl border
				bg-light-surface-secondary dark:bg-[#15171C]
				border-light-border-secondary dark:border-[#22242A]
				outline-none
				focus:border-light-accent-sky focus:dark:border-[#537EFF]
				disabled:opacity-50
				inline-flex flex-row items-center justify-between gap-2 min-w-[180px]
			"
		>
			<Text
				className="
					text-[14px] leading-[20px] font-normal
					text-light-text-primary dark:text-[#F5F6F8]
				"
			>
				{current?.label ?? ""}
			</Text>
			{/*
				Lucide icons take a literal color prop — they don't
				inherit `text-*` utilities. We render a muted neutral
				(#8E8E93 — design-system text-tertiary) which reads
				well on both dark (#11131A) and light (#FFFFFF) chrome.
				A future iteration can route this through
				`useColorScheme()` if the contrast becomes an issue.
			*/}
			<ChevronDown size={16} strokeWidth={1.75} color="#8E8E93" />
		</Pressable>
	);
}
